// src/features/variant_workflow/clinical_observation/hooks/useReportCategoryModalLogic.ts
import { notification } from 'antd';
import React from 'react';
import { useParams } from 'react-router';

import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import { useGetReportVariantCategoryOptionsQuery } from '../../../api/globalDataApi';
import { useUpdateVariantReportCategoryMutation } from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';
import { VariantProbandReportData, VariantSample } from '../../redux';

interface FormValues {
  report_category: string | undefined;
}

interface UseReportCategoryModalLogicProps {
  onClose: () => void;
  reportData: VariantProbandReportData;
  variantSamples: VariantSample[];
  selectedRecord: VariantSample;
  setSelectedRecord: React.Dispatch<
    React.SetStateAction<VariantSample | undefined>
  >;
  setReportData: React.Dispatch<
    React.SetStateAction<VariantProbandReportData | undefined>
  >;
}

/**
 * Custom hook that encapsulates all the business logic for ReportCategoryModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useReportCategoryModalLogic = ({
  onClose,
  reportData,
  variantSamples,
  selectedRecord,
  setSelectedRecord,
  setReportData,
}: UseReportCategoryModalLogicProps) => {
  const params = useParams();

  const [updateVariantReportCategoryMutation] =
    useUpdateVariantReportCategoryMutation();

  const {
    isLoading: isReportModalLoading,
    setIsLoading: setIsReportModalLoading,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose,
    successMessage: NOTIFICATION_MESSAGES.REPORT_CATEGORY_UPDATED,
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { data: reportCategoryOptions = [] } =
    useGetReportVariantCategoryOptionsQuery();

  const modalOpts = {
    ...createModalOpts('Please select a report category for the panel.'),
  };

  const initialValues: FormValues = {
    report_category: reportData.report_category,
  };

  const handleFormSubmit = async (values: any) => {
    if (isReportModalLoading) {
      return;
    }

    if (user && reportData.id) {
      try {
        setIsReportModalLoading(true);

        await updateVariantReportCategoryMutation({
          reportvariantcategory_id: reportData.id,
          requestData: {
            report_category: values.report_category,
          },
          variant_id: params.vid ? Number(params.vid) : undefined,
        }).unwrap();

        // The mutation's invalidatesTags marks the matching query tags stale, so
        // the samples list refetches automatically with no manual state patching

        setSelectedRecord(undefined);
        setReportData(undefined);

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: NOTIFICATION_MESSAGES.REPORT_CATEGORY_UPDATED,
        });

        onClose();
      } catch (error) {
        console.error(error);
      } finally {
        setIsReportModalLoading(false);
      }
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  return {
    isReportModalLoading,
    user,
    stageInfo,
    reportCategoryOptions,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  };
};

export default useReportCategoryModalLogic;
